import ArrowLeft from '@assets/icons/arrow-left.svg';
import DeleteIcon from '@assets/icons/delete.svg';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { APP_MODAL_ROUTES, ORDER_INBOUNDS_ROUTES } from '@/navigators/routes';
import {
  AppModalStackParams,
  OrderInboundsStackParams,
  PaymentsStackParams,
} from '@/navigators/types';
import { ModalContainer } from '@/components/modal-container';
import { PadPaymentForm, PadPaymentFormView } from './pad-payment-form';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { StyleSheet, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/theme-context';

// Registered in both the app modal stack and the order agent stack, which
// cannot reach each other.
type Props =
  | StackScreenProps<AppModalStackParams, APP_MODAL_ROUTES.AddPAD>
  | StackScreenProps<OrderInboundsStackParams, ORDER_INBOUNDS_ROUTES.AddPAD>;

const getStepInfo = (view: PadPaymentFormView) => {
  const totalSteps = 3;
  switch (view) {
    case PadPaymentFormView.ContactInformation:
      return { currentStep: 1, totalSteps };
    case PadPaymentFormView.PaymentInfo:
      return { currentStep: 2, totalSteps };
    case PadPaymentFormView.Agreement:
    case PadPaymentFormView.AdditionalTerms:
      return { currentStep: 3, totalSteps };
    default:
      return { currentStep: 1, totalSteps };
  }
};

export const AddPADScreen: React.FC<Props> = ({ route }) => {
  const { onAddPADPaymentMethod } = route.params;
  const navigation = useNavigation<StackNavigationProp<PaymentsStackParams>>();
  const { bottom } = useSafeAreaInsets();
  const theme = useTheme();

  const [currentView, setCurrentView] = useState(
    PadPaymentFormView.ContactInformation,
  );

  const stepInfo = getStepInfo(currentView);

  const handleBackPress = () => {
    switch (currentView) {
      case PadPaymentFormView.PaymentInfo:
        setCurrentView(PadPaymentFormView.ContactInformation);
        break;
      case PadPaymentFormView.Agreement:
        setCurrentView(PadPaymentFormView.PaymentInfo);
        break;
      case PadPaymentFormView.AdditionalTerms:
        setCurrentView(PadPaymentFormView.Agreement);
        break;
      default:
        navigation.goBack();
    }
  };

  const isAdditionalTermsView =
    currentView === PadPaymentFormView.AdditionalTerms;
  const isContactInfoView =
    currentView === PadPaymentFormView.ContactInformation;

  return (
    <ModalContainer
      navigationHeaderProps={{
        actions: isAdditionalTermsView
          ? []
          : [
              {
                component: (
                  <Typography
                    color={theme.sk.color.text.tertiary}
                    variant="label-md"
                  >
                    {i18n.t('step_x_of_y', {
                      current: stepInfo.currentStep,
                      total: stepInfo.totalSteps,
                    })}
                  </Typography>
                ),
              },
            ],
        BackButtonIcon: isContactInfoView ? DeleteIcon : ArrowLeft,
        backButtonTestID: isContactInfoView
          ? 'pad-action-sheet-close-button'
          : 'pad-action-sheet-back-button',
        centerTitle: true,
        onBackPress: handleBackPress,
        showBackButton: true,
        title: isAdditionalTermsView ? undefined : i18n.t('add_pad_ach'),
      }}
    >
      <View style={[styles.container, { paddingBottom: bottom }]}>
        <PadPaymentForm
          currentView={currentView}
          onChangeView={setCurrentView}
          onClose={() => {
            // goBack must be called here first to dismiss the modal
            // before any additional navigation can occur
            navigation.goBack();
            onAddPADPaymentMethod?.();
          }}
        />
      </View>
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 12,
  },
});
